import type {
  DataTableColumn,
  DataTablePagination,
  DataTableSortActive,
} from '@sparklayer-io/spark-dash-ui/components/ui/data-table/types';
import { useModal } from '@sparklayer-io/spark-dash-ui/hooks/use-modal';
import { usePagination } from '@sparklayer-io/spark-dash-ui/hooks/use-pagination';
import { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import { Badge, DataTable, Text } from '@ui/themed';
import { t } from 'i18next';
import cloneDeep from 'lodash/cloneDeep';
import { useMemo, useState } from 'react';
import type { PartialBy } from '../../../types/util';
import { type Discount, type DiscountTypeMetaID, discountTypes } from '../types';
import { DiscountsDeleteModal } from './discounts-delete-modal';
import { DiscountsRow } from './discounts-row';

const sortOptions = [
  { id: 'default', label: t('features:discounts.components.list_table.sort.default') },
  { id: 'name', label: t('features:discounts.components.list_table.sort.name') },
] as const;

const typeFilters = [
  {
    id: 'order_level_get_amount_off',
    label: t('features:discounts.components.list_table.filters.types.order_level_get_amount_off'),
  },
  {
    id: 'order_level_get_free_product',
    label: t('features:discounts.components.list_table.filters.types.order_level_get_free_product'),
  },
  {
    id: 'product_level_get_amount_off',
    label: t('features:discounts.components.list_table.filters.types.product_level_get_amount_off'),
  },
  {
    id: 'order_level_get_shipping_reward',
    label: t(
      'features:discounts.components.list_table.filters.types.order_level_get_shipping_reward',
    ),
  },
  {
    id: 'advanced_order_level_get_amount_off',
    label: t(
      'features:discounts.components.list_table.filters.types.advanced_order_level_get_amount_off',
    ),
  },
  {
    id: 'order_level_requirement_amount_off',
    label: t(
      'features:discounts.components.list_table.filters.types.order_level_requirement_amount_off',
    ),
  },
  {
    id: 'advanced_discount',
    label: t('features:discounts.components.list_table.filters.types.advanced_discount'),
  },
  {
    id: 'advanced_free_products',
    label: t('features:discounts.components.list_table.filters.types.advanced_free_products'),
  },
];

function getActiveDiscounts(discounts: Discount[]): Discount[] {
  return discounts.filter((discount) => isDiscountCurrentlyActive(discount));
}

function getInactiveDiscounts(discounts: Discount[]): Discount[] {
  return discounts.filter((discount) => !isDiscountCurrentlyActive(discount));
}

function isDiscountCurrentlyActive(discount: Discount): boolean {
  return (
    discount.active &&
    (!discount.start_date || new Date(discount.start_date) < new Date()) &&
    (!discount.end_date || new Date(discount.end_date) > new Date())
  );
}

function duplicateDiscount(
  discount: Discount,
  discounts: Discount[],
  openDuplicateModal: (template: Partial<Discount>, discounts: Discount[]) => void,
) {
  const { id, ...rest } = cloneDeep(discount);
  const newDiscount: PartialBy<Discount, 'id'> = rest;

  const discountNames = discounts.map((x) => x.internal_name);

  let newInternalName = `${discount.internal_name} Copy`;
  let copyNumber = 2;
  while (discountNames.includes(newInternalName)) {
    newInternalName = `${discount.internal_name} Copy ${copyNumber++}`;
  }
  newDiscount.internal_name = newInternalName;

  internalSlugEnsureUnique(discount, discounts);
  openDuplicateModal(newDiscount, discounts);
}

function internalSlugEnsureUnique(discount: Discount, discounts: Discount[]) {
  const baseSlug = discount.internal_slug;
  if (!baseSlug) {
    return;
  }

  discount.internal_slug = ensureUniqueSlug(baseSlug, discounts);
}

function ensureUniqueSlug(baseSlug: string, existingDiscounts: Array<Partial<Discount>>): string {
  if (!baseSlug) return baseSlug;

  let counter = 1;
  let slug = baseSlug;

  const isTaken = (s: string) => existingDiscounts.some((d) => d.internal_slug === s);

  while (isTaken(slug)) {
    counter += 1;
    slug = `${baseSlug}-${counter}`;
  }

  return slug;
}

function applyDiscountView(discounts: Discount[], view: DiscountView) {
  switch (view) {
    case 'all':
      return discounts;
    case 'active':
      return getActiveDiscounts(discounts);
    case 'inactive':
      return getInactiveDiscounts(discounts);
  }
}

type DiscountStatus = 'active' | 'inactive';

type DiscountView = 'all' | DiscountStatus;

function applyDiscountFilters(discounts: Discount[], types: string[]) {
  if (types.length === 0) return discounts;
  return filterDiscounts(
    discounts,
    (row) =>
      types.length === 0 ||
      types.some((tag) => (row.template! as DiscountTypeMetaID).includes(tag)),
  );
}

function filterDiscounts(discounts: Discount[], keep: (row: Discount) => boolean) {
  return discounts.filter(keep);
}

function applyDiscountSearch(discounts: Discount[], query: string): Discount[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return discounts;

  return discounts.filter((discount) => discount.internal_name.toLowerCase().includes(needle));
}

function applyDiscountSort(
  discounts: Discount[],
  sort: DataTableSortActive<DiscountSortId> | undefined,
) {
  if (!sort) return discounts;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...discounts].sort((a, b) =>
    sort.id === 'name'
      ? a.internal_name.localeCompare(b.internal_name) * dir
      : (b.priority - a.priority || a.internal_name.localeCompare(b.internal_name)) * dir,
  );
}

type DiscountSortId = 'default' | 'name';

type DiscountsTableComponentProps = {
  title: string;
  discounts: Discount[];
  onEditDiscount: (discount: Discount, discounts: Discount[]) => void;
  onCreateDiscountFromTemplate: (template: Partial<Discount>, discounts: Discount[]) => void;
};

export function DiscountsTable({
  title,
  discounts,
  onEditDiscount,
  onCreateDiscountFromTemplate,
}: DiscountsTableComponentProps) {
  const [query, setQuery] = useState('');
  const [view, setView] = useState<DiscountView>('all');
  const [sort, setSort] = useState<DataTableSortActive<DiscountSortId> | undefined>(undefined);
  const [discountToDelete, setDiscountToDelete] = useState<Discount | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const filtered = useMemo(
    () => applyDiscountFilters(applyDiscountSearch(discounts, query), types),
    [discounts, query, types],
  );
  const discountsToShow = useMemo(
    () => applyDiscountSort(applyDiscountView(filtered, view), sort),
    [filtered, view, sort],
  );

  const paged = usePagination<Discount>(discountsToShow, {
    pageSize: 10,
    pageSizeOptions: [10, 25, 30],
  });

  const size = paged.pagination?.per_page ?? PaginationOptions.DEFAULT_PAGE_SIZE;

  const pagination: DataTablePagination = {
    page: paged.page,
    total: paged.pagination?.total ?? 0,
    pageSize: size,
    pageSizeOptions: paged.pageSizeOptions,
    onPageChange: ({ page, pageSize }) => {
      if (pageSize !== size) {
        paged.setPageSize(pageSize);
        return;
      }
      paged.setPage(page);
    },
  };

  const viewCounts = {
    all: filtered.length,
    active: getActiveDiscounts(filtered).length,
    inactive: getInactiveDiscounts(filtered).length,
  };

  const viewOptions = [
    {
      id: 'all',
      label: t('features:discounts.components.list_table.views.all'),
      count: viewCounts.all,
    },
    {
      id: 'active',
      label: t('features:discounts.components.list_table.views.active'),
      count: viewCounts.active,
    },
    {
      id: 'inactive',
      label: t('features:discounts.components.list_table.views.inactive'),
      count: viewCounts.inactive,
    },
  ] as const;

  const discountStatusBadge: Record<
    DiscountStatus,
    { label: string; variant: 'green' | 'default' }
  > = {
    active: { label: t('features:discounts.components.list_table.views.active'), variant: 'green' },
    inactive: {
      label: t('features:discounts.components.list_table.views.inactive'),
      variant: 'default',
    },
  };

  const columns: DataTableColumn<Discount>[] = [
    {
      id: 'internal_name',
      label: t('features:discounts.components.list_table.headings.internal_name'),
      alwaysVisible: true,
      renderCell: (row: Discount) => (
        <DataTable.Cell>
          <Text truncate>{row.internal_name}</Text>
        </DataTable.Cell>
      ),
    },
    {
      id: 'type',
      label: t('features:discounts.components.list_table.headings.type'),
      alwaysVisible: true,
      renderCell: (row: Discount) => (
        <DataTable.Cell>
          <Text truncate>{discountTypes[row.template! as DiscountTypeMetaID]?.label}</Text>
        </DataTable.Cell>
      ),
    },
    {
      id: 'requirements',
      label: t('features:discounts.components.list_table.headings.requirements'),
      alwaysVisible: true,
      interactive: true,
      renderCell: (row: Discount) => (
        <DataTable.Cell>
          <DiscountsRow discount={row} discounts={discounts} />
        </DataTable.Cell>
      ),
    },
    {
      id: 'status',
      label: t('features:discounts.components.list_table.headings.status'),
      alwaysVisible: true,
      renderCell: (row: Discount) => (
        <DataTable.Cell>
          <Badge
            variant={
              discountStatusBadge[isDiscountCurrentlyActive(row) ? 'active' : 'inactive'].variant
            }
          >
            {discountStatusBadge[isDiscountCurrentlyActive(row) ? 'active' : 'inactive'].label}
          </Badge>
        </DataTable.Cell>
      ),
    },
    {
      id: 'actions',
      alwaysVisible: true,
      sticky: true,
      interactive: true,
      renderCell: (row: Discount) => (
        <DataTable.Cell align="end">
          <DataTable.Actions
            actions={[
              {
                id: 'edit',
                label: t('buttons:edit'),
                onClick: () => onEditDiscount(row, discounts),
              },
              {
                id: 'duplicate',
                label: t('buttons:duplicate'),
                icon: 'duplicate',
                inMoreMenu: true,
                onClick: () => duplicateDiscount(row, discounts, onCreateDiscountFromTemplate),
              },
              {
                id: 'delete',
                label: t('buttons:delete'),
                icon: 'delete',
                danger: true,
                inMoreMenu: true,
                onClick: () => {
                  setDiscountToDelete(row);
                  openDeleteModal();
                },
              },
            ]}
          />
        </DataTable.Cell>
      ),
    },
  ];

  return (
    <div>
      <DiscountsDeleteModal
        discount={discountToDelete}
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
      />
      <DataTable
        caption={title}
        columns={columns}
        data={paged.data}
        getRowId={(row) => row.id}
        search={{
          placeholder: t('features:discounts.components.list_table.search'),
          value: query,
          onChange: setQuery,
        }}
        filters={[
          {
            id: 'types',
            label: t('features:discounts.components.list_table.filters.type'),
            options: typeFilters,
            value: types,
            pinned: true,
            multiple: true,
            onChange: setTypes,
          },
        ]}
        views={{
          items: viewOptions,
          value: view,
          onChange: setView,
        }}
        sort={{
          options: sortOptions,
          value: sort,
          onChange: setSort,
        }}
        pagination={pagination}
      />
    </div>
  );
}
